import React, { useState, useEffect } from 'react';
import { Container, Table, Button, ButtonGroup } from 'react-bootstrap';
import { FaEnvelope, FaPhone, FaFileCsv, FaFileExcel, FaFilePdf } from 'react-icons/fa';
import api from '../services/api';
import { getMediaUrl } from '../services/api';
import './Employees.css';

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    try {
      const response = await api.get('/employees/');
      const data = response.data.results || response.data;
      setEmployees(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading employees:', error);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Position', 'Department', 'Qualification', 'Email', 'Phone'];
    const csvData = employees.map(emp => [
      emp.name,
      emp.position,
      emp.department_name || '',
      emp.qualification || '',
      emp.email || '',
      emp.phone || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `employees_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const exportToExcel = () => {
    // Create HTML table for Excel
    const headers = '<tr><th>Name</th><th>Position</th><th>Department</th><th>Qualification</th><th>Email</th><th>Phone</th></tr>';
    const rows = employees.map(emp => `
      <tr>
        <td>${emp.name}</td>
        <td>${emp.position}</td>
        <td>${emp.department_name || ''}</td>
        <td>${emp.qualification || ''}</td>
        <td>${emp.email || ''}</td>
        <td>${emp.phone || ''}</td>
      </tr>
    `).join('');

    const table = `<table>${headers}${rows}</table>`;
    const blob = new Blob([table], { type: 'application/vnd.ms-excel' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `employees_${new Date().toISOString().split('T')[0]}.xls`;
    link.click();
  };

  const exportToPDF = () => {
    window.print();
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </Container>
    );
  }

  return (
    <Container className="employees-page pb-5">
      <div className="employee-title-section">
        <div>
          <h1 className="mb-1">Employee Directory</h1>
          <p className="text-muted small mb-0">
            Complete list of {employees.length} municipal corporation employees
          </p>
        </div>
        <ButtonGroup className="shadow-sm">
          <Button variant="outline-success" size="sm" onClick={exportToCSV}>
            <FaFileCsv className="me-1" /> CSV
          </Button>
          <Button variant="outline-primary" size="sm" onClick={exportToExcel}>
            <FaFileExcel className="me-1" /> Excel
          </Button>
          <Button variant="outline-danger" size="sm" onClick={exportToPDF}>
            <FaFilePdf className="me-1" /> PDF
          </Button>
        </ButtonGroup>
      </div>

      <div className="table-responsive shadow-sm">
        <Table striped bordered hover className="employee-table align-middle">
          <thead className="employee-table-header">
            <tr>
              <th>Photo</th>
              <th>Name</th>
              <th>Position</th>
              <th>Department</th>
              <th>Qualification</th>
              <th>Contact</th>
            </tr>
          </thead>
          <tbody>
            {employees.map((employee) => (
              <tr key={employee.id}>
                <td className="text-center">
                  {employee.photo ? (
                    <img
                      src={getMediaUrl(employee.photo)}
                      alt={employee.name}
                      className="employee-photo-thumb"
                    />
                  ) : (
                    <div className="employee-photo-placeholder">N/A</div>
                  )}
                </td>
                <td><strong>{employee.name}</strong></td>
                <td>{employee.position}</td>
                <td>{employee.department_name || '-'}</td>
                <td>{employee.qualification || '-'}</td>
                <td>
                  {employee.phone && (
                    <div className="mb-1">
                      <FaPhone className="me-2 text-success" />
                      <a href={`tel:${employee.phone}`}>{employee.phone}</a>
                    </div>
                  )}
                  {employee.email && (
                    <div>
                      <FaEnvelope className="me-2 text-primary" />
                      <a href={`mailto:${employee.email}`}>{employee.email}</a>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {employees.length === 0 && (
        <div className="text-center py-5">
          <p className="text-muted">No employee records available at this time.</p>
        </div>
      )}
    </Container>
  );
};

export default Employees;
